import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PublicLayout } from "./components/PublicLayout";
import { Loading } from "./components/ui";
import { RequireUser, WorkspaceLayout } from "./components/WorkspaceLayout";
import { LangProvider } from "./lib/i18n";
import { OfflineQueueRunner } from "./components/OfflineQueueRunner";
import { SessionProvider } from "./lib/session";
import Home from "./pages/public/Home";
import { About, NotFound, Privacy, Standards, Terms } from "./pages/public/Info";
import SignIn, { RecoverAccess } from "./pages/public/SignIn";
import { AppErrorBoundary } from "./components/AppErrorBoundary";

// Public pages that are not on the landing path, and every signed-in surface,
// are split into their own chunks so the homepage stays light. If a chunk
// fails to arrive (typically a deploy replaced it), the page reloads once.
const RELOADED_KEY = "seva.chunkReload";
function loadChunk<T>(load: () => Promise<T>): Promise<T> {
  return load().then(
    (m) => {
      try {
        sessionStorage.removeItem(RELOADED_KEY);
      } catch {
        /* private mode */
      }
      return m;
    },
    (err) => {
      let reloaded = false;
      try {
        reloaded = sessionStorage.getItem(RELOADED_KEY) === location.pathname;
        sessionStorage.setItem(RELOADED_KEY, location.pathname);
      } catch {
        /* private mode: still reload once below */
      }
      if (reloaded) throw err;
      location.reload();
      return new Promise<T>(() => undefined);
    },
  );
}
const named = <T extends Record<string, any>>(load: () => Promise<T>, key: keyof T) =>
  lazy(() => loadChunk(load).then((m) => ({ default: m[key] })));

const pub = () => import("./pages/public/Record");
const prom = () => import("./pages/public/Promises");
const join = () => import("./pages/public/Join");
const cit = () => import("./pages/account/Citizen");
const cert = () => import("./pages/account/Certificate");
const sec = () => import("./pages/account/Security");
const cases = () => import("./pages/workspace/Cases");
const ops = () => import("./pages/workspace/Operations");
const roles = () => import("./pages/workspace/Roles");
const adm = () => import("./pages/admin/Admin");
const geo = () => import("./pages/public/Constituencies");

const ComplaintPage = lazy(() => loadChunk(() => import("./pages/public/Complaint")));
const TrackComplaint = named(pub, "TrackComplaint");
const CivicRecord = named(pub, "CivicRecord");
const PublicComplaint = named(pub, "PublicComplaint");
const ProblemFeed = named(() => import("./pages/public/Feed"), "ProblemFeed");
const Representatives = named(pub, "Representatives");
const RepresentativeProfile = named(pub, "RepresentativeProfile");
const ClaimProfile = named(() => import("./pages/public/Claim"), "ClaimProfile");
const Constituencies = named(geo, "Constituencies");
const ConstituencyPage = named(geo, "ConstituencyPage");
const PromiseNew = named(prom, "PromiseNew");
const PromiseRegistry = named(prom, "PromiseRegistry");
const PromiseDetail = named(prom, "PromiseDetail");
const JoinHub = named(join, "JoinHub");
const FoundingCitizenForm = named(join, "FoundingCitizenForm");
const ExpertForm = named(join, "ExpertForm");
const CivilSocietyForm = named(join, "CivilSocietyForm");
const ConnectForm = named(join, "ConnectForm");
const SecurityStepUp = lazy(() => loadChunk(sec));
const MyArea = named(cit, "MyArea");
const MyComplaints = named(cit, "MyComplaints");
const MyComplaintDetail = named(cit, "MyComplaintDetail");
const MyPromises = named(cit, "MyPromises");
const InboxPage = named(cit, "InboxPage");
const ProfilePage = named(cit, "ProfilePage");
const Participate = named(cit, "Participate");
const CertificatePage = named(cert, "CertificatePage");
const CaseQueue = named(cases, "CaseQueue");
const CaseDetail = named(cases, "CaseDetail");
const PromiseReview = named(ops, "PromiseReview");
const TasksPage = named(ops, "TasksPage");
const AccessPage = named(ops, "AccessPage");
const DocketsPage = named(ops, "DocketsPage");
const DocketDetail = named(ops, "DocketDetail");
const AnalyticsPage = named(ops, "AnalyticsPage");
const WorkspaceHome = named(roles, "WorkspaceHome");
const AssociatesPage = named(roles, "AssociatesPage");
const CommandCenter = named(adm, "CommandCenter");
const UsersPage = named(adm, "UsersPage");
const UserDetail = named(adm, "UserDetail");
const ApplicationsPage = named(adm, "ApplicationsPage");
const GovernancePage = named(adm, "GovernancePage");
const ClaimsPage = named(adm, "ClaimsPage");
const RoleDefaultsPage = named(adm, "RoleDefaultsPage");
const GeographyAdmin = named(adm, "GeographyAdmin");
const AuditPage = named(adm, "AuditPage");
const SettingsPage = named(adm, "SettingsPage");
const DataPage = named(adm, "DataPage");
const CommunicationsPage = named(adm, "CommunicationsPage");
const ServicesPage = named(adm, "ServicesPage");
const ModerationPage = named(adm, "ModerationPage");

function ScrollToTop() {
  const { pathname } = useLocation();
  // Braces matter: Chrome 154 made scrollTo return a promise, and an effect that
  // returns anything but a function crashes React when the page unmounts.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <SessionProvider>
          <ScrollToTop />
          <OfflineQueueRunner />
          <AppErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route index element={<Home />} />
                <Route path="about" element={<About />} />
                <Route path="standards" element={<Standards />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<Terms />} />
                <Route path="signin" element={<SignIn />} />
                <Route path="recover" element={<RecoverAccess />} />
                <Route path="complaint" element={<ComplaintPage />} />
                <Route path="complaint/track" element={<TrackComplaint />} />
                <Route path="promise/new" element={<PromiseNew />} />
                <Route path="promises" element={<PromiseRegistry />} />
                <Route path="promises/:id" element={<PromiseDetail />} />
                <Route path="feed" element={<ProblemFeed />} />
                <Route path="record" element={<CivicRecord />} />
                <Route path="record/:id" element={<PublicComplaint />} />
                <Route path="representatives" element={<Representatives />} />
                <Route path="representatives/:id" element={<RepresentativeProfile />} />
                <Route path="representatives/:id/claim" element={<ClaimProfile />} />
                <Route path="constituencies" element={<Constituencies />} />
                <Route path="constituencies/:id" element={<ConstituencyPage />} />
                <Route path="join" element={<JoinHub />} />
                <Route path="join/founding-citizen" element={<FoundingCitizenForm />} />
                <Route path="join/expert" element={<ExpertForm />} />
                <Route path="join/civil-society" element={<CivilSocietyForm />} />
                <Route path="connect" element={<ConnectForm />} />
                <Route
                  path="security"
                  element={
                    <RequireUser>
                      <SecurityStepUp />
                    </RequireUser>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route element={<WorkspaceLayout />}>
                <Route path="account" element={<MyArea />} />
                <Route path="account/complaints" element={<MyComplaints />} />
                <Route path="account/complaints/:id" element={<MyComplaintDetail />} />
                <Route path="account/promises" element={<MyPromises />} />
                <Route path="account/inbox" element={<InboxPage />} />
                <Route path="account/profile" element={<ProfilePage />} />
                <Route path="account/participate" element={<Participate />} />
                <Route path="account/certificate" element={<CertificatePage />} />

                <Route path="workspace" element={<WorkspaceHome />} />
                <Route path="workspace/cases" element={<CaseQueue />} />
                <Route path="workspace/cases/:id" element={<CaseDetail />} />
                <Route path="workspace/promises" element={<PromiseReview />} />
                <Route path="workspace/tasks" element={<TasksPage />} />
                <Route path="workspace/access" element={<AccessPage />} />
                <Route path="workspace/dockets" element={<DocketsPage />} />
                <Route path="workspace/dockets/:id" element={<DocketDetail />} />
                <Route path="workspace/analytics" element={<AnalyticsPage />} />
                <Route path="workspace/associates" element={<AssociatesPage />} />
                <Route path="workspace/admin" element={<CommandCenter />} />
                <Route path="workspace/admin/users" element={<UsersPage />} />
                <Route path="workspace/admin/users/:id" element={<UserDetail />} />
                <Route path="workspace/admin/applications" element={<ApplicationsPage />} />
                <Route path="workspace/admin/governance" element={<GovernancePage />} />
                <Route path="workspace/admin/claims" element={<ClaimsPage />} />
                <Route path="workspace/admin/role-permissions" element={<RoleDefaultsPage />} />
                <Route path="workspace/admin/geography" element={<GeographyAdmin />} />
                <Route path="workspace/admin/audit" element={<AuditPage />} />
                <Route path="workspace/admin/settings" element={<SettingsPage />} />
                <Route path="workspace/admin/data" element={<DataPage />} />
                <Route path="workspace/admin/communications" element={<CommunicationsPage />} />
                <Route path="workspace/admin/services" element={<ServicesPage />} />
                <Route path="workspace/admin/moderation" element={<ModerationPage />} />
                <Route path="workspace/*" element={<Navigate to="/workspace" replace />} />
              </Route>
            </Routes>
          </Suspense>
          </AppErrorBoundary>
        </SessionProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
